import { useDebounce } from '@/hooks'
import { useJobillaOrgId } from '@/hooks'
import { ActivitySummary } from '@/shared/activity-summary'
import { CandidateQuickPeek, type CandidateQuickPeekData } from '@/shared/candidate-quick-peek'
import { logger, MainNav, SubtleLoader } from '@/ui'
import { JobibotInput } from '@/ui/global-chat/JobibotInput'
import {
	type LastActivityFilter,
	personIndexQueryKey,
	type PersonSummaryData,
	usePersonIndex,
	usePersonShow,
} from '@jobilla/nexus-sdk'
import { useBooleanFlagValue } from '@openfeature/react-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from '@tanstack/react-router'
import {
	ChevronLeftIcon,
	ChevronRightIcon,
	DownloadIcon,
	LayoutGridIcon,
	LayoutListIcon,
	Loader2Icon,
	PanelLeftIcon,
	RefreshCwIcon,
	SearchIcon,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

type ActiveView = 'sidebar' | 'table' | 'cards'

type TalentPoolPageProps = {
	search: { q?: string; last_activity: LastActivityFilter; page: number; peek?: string }
	companyId: string
}

export function TalentPoolPage({ search, companyId }: TalentPoolPageProps) {
	const { t } = useTranslation('common')
	const navigate = useNavigate()
	const organisationId = useJobillaOrgId()
	const queryClient = useQueryClient()
	const globalAiChatEnabled = useBooleanFlagValue('global-ai-chat', false)

	const [searchInput, setSearchInput] = useState(search.q ?? '')
	const debouncedSearch = useDebounce(searchInput, 300) as string
	const searchPending = searchInput.trim() !== (search.q ?? '')

	// Tracks the q value we (this component) last pushed to the URL. Anything
	// else (browser back/forward, deep link, programmatic nav from elsewhere)
	// must have come from outside, so we resync the input. Using a ref instead
	// of comparing debouncedSearch directly avoids a race during the debounce →
	// nav window where the URL hasn't caught up yet and the sync effect would
	// otherwise clobber the just-typed value.
	const lastNavigatedQRef = useRef(search.q ?? '')

	useEffect(() => {
		const urlValue = search.q ?? ''
		if (urlValue === lastNavigatedQRef.current) {
			return
		}
		lastNavigatedQRef.current = urlValue
		setSearchInput(urlValue)
	}, [search.q])

	useEffect(() => {
		const next = debouncedSearch.trim() === '' ? undefined : debouncedSearch.trim()
		if (next === search.q) {
			return
		}
		if (next !== undefined) {
			logger.track('Talent pool: Searched')
		}
		lastNavigatedQRef.current = next ?? ''
		void navigate({
			to: '/company/$companyId/talent-pool',
			params: { companyId },
			search: (prev) => ({ ...prev, q: next, page: 1 }),
			replace: true,
		})
	}, [debouncedSearch, search.q, navigate, companyId])

	const queryParams = {
		q: search.q,
		last_activity: search.last_activity,
		page: search.page,
		per_page: 25,
	}

	const handleLastActivityChange = (next: LastActivityFilter) => {
		logger.track('Talent pool: Filtered', { data: { last_activity: next } })
		void navigate({
			to: '/company/$companyId/talent-pool',
			params: { companyId },
			search: (prev) => ({ ...prev, last_activity: next, page: 1 }),
			replace: true,
		})
	}

	const { data, isLoading, isError, isFetching, isPlaceholderData, refetch } = usePersonIndex(
		organisationId,
		queryParams,
		{ enabled: !!organisationId, placeholderData: (previous) => previous },
	)

	const peekId = search.peek
	const [summaryGenerating, setSummaryGenerating] = useState(false)
	const summaryAtGenerationStart = useRef<string | null | undefined>(undefined)
	const summaryPollAttempts = useRef(0)

	const MAX_SUMMARY_POLL_ATTEMPTS = 30

	const peekQuery = usePersonShow(organisationId, peekId ?? '', {
		enabled: !!organisationId && !!peekId,
		refetchInterval: summaryGenerating ? 2000 : false,
	})

	useEffect(() => {
		if (!summaryGenerating || summaryAtGenerationStart.current === undefined) {
			return
		}
		if (peekQuery.data?.summary !== summaryAtGenerationStart.current) {
			setSummaryGenerating(false)
			summaryAtGenerationStart.current = undefined
			summaryPollAttempts.current = 0
			return
		}
		summaryPollAttempts.current += 1
		if (summaryPollAttempts.current >= MAX_SUMMARY_POLL_ATTEMPTS) {
			logger.warn('Talent pool: Summary poll timed out without update', {
				data: { person_id: peekId, organisation_id: organisationId },
			})
			setSummaryGenerating(false)
			summaryAtGenerationStart.current = undefined
			summaryPollAttempts.current = 0
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- data?.summary is intentionally omitted: adding it would cause the effect to only fire when the string changes, re-introducing the stuck-polling bug this effect fixes
	}, [peekQuery.dataUpdatedAt, peekQuery.errorUpdatedAt, summaryGenerating])

	useEffect(() => {
		setSummaryGenerating(false)
		summaryAtGenerationStart.current = undefined
		summaryPollAttempts.current = 0
	}, [peekId])

	const peekData: CandidateQuickPeekData | null = useMemo(() => {
		if (!peekQuery.data) {
			return null
		}
		const earliestCreated = peekQuery.data.candidacies
			.map((candidacy) => candidacy.createdAt)
			.filter((value): value is string => value !== null)
			.sort()[0] ?? null
		// previous_names is append-only on the backend (chronological history of
		// every overwrite). For UI display we dedupe by normalised value and drop
		// anything that matches the current displayed name, so "Also known as"
		// only shows genuinely different prior names.
		const currentNameKey = peekQuery.data.name?.trim().toLowerCase() ?? ''
		const seen = new Set<string>(currentNameKey ? [currentNameKey] : [])
		const previousNames: string[] = []
		for (const entry of peekQuery.data.previousNames) {
			const value = entry.value.trim()
			if (value === '') {
				continue
			}
			const key = value.toLowerCase()
			if (seen.has(key)) {
				continue
			}
			seen.add(key)
			previousNames.push(value)
		}

		return {
			name: peekQuery.data.name,
			joinedAt: earliestCreated,
			summary: peekQuery.data.summary ?? null,
			emails: peekQuery.data.emails.map((email) => ({ value: email.value })),
			phones: peekQuery.data.phones.map((phone) => ({ value: phone.value })),
			skills: peekQuery.data.skills.map((skill) => skill.label),
			candidacies: peekQuery.data.candidacies.map((candidacy) => ({
				candidateId: candidacy.id,
				campaign: { id: candidacy.campaign.id, title: candidacy.campaign.title },
				lastActivity: candidacy.lastActivityLabel
					? {
						label: candidacy.lastActivityLabel,
						at: candidacy.lastActivityAt,
						actorName: candidacy.lastActivityActorName,
					}
					: null,
			})),
			previousNames,
		}
	}, [peekQuery.data])

	// Keep last-shown data around so the drawer has content to render
	// during its exit animation after `peek` is cleared from the URL.
	const [displayedPeekData, setDisplayedPeekData] = useState<CandidateQuickPeekData | null>(null)

	useEffect(() => {
		if (peekId && peekData) {
			setDisplayedPeekData(peekData)
		} else if (peekId && !peekData) {
			setDisplayedPeekData(null)
		}
	}, [peekId, peekData])

	const setPage = (page: number) => {
		void navigate({
			to: '/company/$companyId/talent-pool',
			params: { companyId },
			search: (prev) => ({ ...prev, page }),
			replace: true,
		})
	}

	const setPeek = (personId: string | undefined) => {
		void navigate({
			to: '/company/$companyId/talent-pool',
			params: { companyId },
			search: (prev) => ({ ...prev, peek: personId }),
			replace: true,
			resetScroll: false,
		})
	}

	const previewCandidate = (personId: string) => {
		logger.track('Talent pool: Candidate previewed', {
			data: { person_id: personId },
		})
		setPeek(personId)
	}

	const refresh = () => {
		void queryClient.invalidateQueries({ queryKey: personIndexQueryKey(organisationId) })
		void refetch()
	}

	const totalCount = data?.meta.total ?? 0
	const currentPage = data?.meta.currentPage ?? search.page
	const lastPage = data?.meta.lastPage ?? 1
	const perPage = data?.meta.perPage ?? 25

	return (
		<MainNav activeMainNavLink="talentPool">
			<div className="bg-grey-50 min-h-(--screen-height)">
				<div className="px-10 py-8 space-y-4">
					<TalentPoolHeader
						isFetching={isFetching}
						onRefresh={refresh}
					/>

					<div className="flex flex-col md:flex-row gap-4 md:items-start">
						<TalentPoolSidebar
							searchValue={searchInput}
							onSearchChange={setSearchInput}
							lastActivity={search.last_activity}
							onLastActivityChange={handleLastActivityChange}
						/>
						<div className="flex-1 min-w-0">
							<div className="flex items-start">
								<div className="flex-1 min-w-0">
									{globalAiChatEnabled
										? (
											<div className="mb-4">
												<JobibotInput
													placeholder={t('talent_pool.jobibot_placeholder')}
													source="talent-pool"
												/>
											</div>
										)
										: null}
									{isError
										? <p className="text-sm text-red-600">{t('talent_pool.error')}</p>
										: null}
									<div className="relative">
										{isLoading
											? (
												<div className="bg-white rounded-md border border-grey-100 p-8">
													<SubtleLoader delay={0} />
												</div>
											)
											: null}
										{!isLoading && data && data.data.length === 0
											? (
												<div className="bg-white rounded-md border border-grey-100 p-8 text-center text-sm text-grey-500">
													{search.q
														? t('talent_pool.empty_search')
														: t('talent_pool.empty')}
												</div>
											)
											: null}
										{!isLoading && data && data.data.length > 0
											? (
												<div
													className={`space-y-4 transition-opacity duration-150 ${
														isPlaceholderData || searchPending ? 'opacity-50' : ''
													}`}
												>
													<ResultsTable
														people={data.data}
														selectedId={peekId}
														onSelect={previewCandidate}
													/>
													<PaginationBar
														total={totalCount}
														currentPage={currentPage}
														lastPage={lastPage}
														perPage={perPage}
														onPageChange={setPage}
													/>
												</div>
											)
											: null}
										{(isPlaceholderData || searchPending) && !isLoading
											? (
												<div
													className="absolute inset-0 flex items-start justify-center pt-12 z-10"
													aria-live="polite"
													aria-busy="true"
												>
													<div className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-grey-200 rounded-md shadow-sm text-sm text-grey-700">
														<Loader2Icon className="size-4 animate-spin text-brand-600" />
														<span>{t('common.loading')}</span>
													</div>
												</div>
											)
											: null}
									</div>
								</div>

								<CandidateQuickPeek
									open={!!peekId}
									onClose={() => setPeek(undefined)}
									data={displayedPeekData}
									isLoading={!!peekId && peekQuery.isLoading}
									organisationId={organisationId}
									personId={peekId ?? null}
									isSummaryGenerating={summaryGenerating}
									onSummaryRequested={() => {
										summaryAtGenerationStart.current = peekQuery.data?.summary ?? null
										summaryPollAttempts.current = 0
										setSummaryGenerating(true)
									}}
									onExitComplete={() => setDisplayedPeekData(null)}
									renderCandidacyLink={(candidacy) => (
										<Link
											to="/company/$companyId/$campaignId/candidates/$candidateId"
											params={{
												companyId,
												campaignId: candidacy.campaign.id,
												candidateId: candidacy.candidateId,
											}}
											onClick={() =>
												logger.track('Talent pool: Candidate profile opened', {
													data: {
														candidate_id: candidacy.candidateId,
														campaign_id: candidacy.campaign.id,
													},
												})}
										>
											{candidacy.campaign.title}
										</Link>
									)}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</MainNav>
	)
}

function TalentPoolHeader({
	isFetching,
	onRefresh,
}: {
	isFetching: boolean
	onRefresh: () => void
}) {
	const { t } = useTranslation('common')
	const [activeView, setActiveView] = useState<ActiveView>('sidebar')

	return (
		<div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
			<div>
				<h1 className="text-2xl font-medium tracking-tight text-grey-900">
					{t('talent_pool.title')}
				</h1>
			</div>

			<div className="flex items-center gap-2">
				<div className="hidden">
					<ViewSwitcher value={activeView} onChange={setActiveView} />
				</div>
				<button
					type="button"
					className="hidden items-center gap-1.5 px-3 py-2 text-sm text-grey-700 bg-white border border-grey-200 rounded-md cursor-pointer hover:bg-grey-50"
					title={t('talent_pool.export')}
				>
					<DownloadIcon className="size-4" />
					<span>{t('talent_pool.export')}</span>
				</button>
				<button
					type="button"
					onClick={onRefresh}
					disabled={isFetching}
					className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-grey-700 bg-white border border-grey-200 rounded-md cursor-pointer hover:bg-grey-50 disabled:opacity-60 disabled:cursor-not-allowed"
					title={t('talent_pool.refresh')}
				>
					<RefreshCwIcon className={`size-4 ${isFetching ? 'animate-spin' : ''}`} />
					<span className="sr-only">{t('talent_pool.refresh')}</span>
				</button>
			</div>
		</div>
	)
}

function ViewSwitcher({
	value,
	onChange,
}: {
	value: ActiveView
	onChange: (next: ActiveView) => void
}) {
	const { t } = useTranslation('common')

	const buttons: Array<{ key: ActiveView; icon: typeof LayoutListIcon; label: string }> = [
		{ key: 'table', icon: LayoutListIcon, label: t('talent_pool.view.table') },
		{ key: 'sidebar', icon: PanelLeftIcon, label: t('talent_pool.view.sidebar') },
		{ key: 'cards', icon: LayoutGridIcon, label: t('talent_pool.view.cards') },
	]

	return (
		<div className="inline-flex bg-white border border-grey-200 rounded-md overflow-hidden">
			{buttons.map((button) => {
				const Icon = button.icon
				const active = value === button.key
				return (
					<button
						key={button.key}
						type="button"
						onClick={() => onChange(button.key)}
						className={`px-2.5 py-2 text-sm border-r last:border-r-0 border-grey-200 cursor-pointer ${
							active ? 'bg-grey-100 text-grey-900' : 'text-grey-500 hover:bg-grey-50'
						}`}
						title={button.label}
					>
						<Icon className="size-4" />
						<span className="sr-only">{button.label}</span>
					</button>
				)
			})}
		</div>
	)
}

function TalentPoolSidebar({
	searchValue,
	onSearchChange,
	lastActivity,
	onLastActivityChange,
}: {
	searchValue: string
	onSearchChange: (next: string) => void
	lastActivity: LastActivityFilter
	onLastActivityChange: (next: LastActivityFilter) => void
}) {
	const { t } = useTranslation('common')

	return (
		<aside className="md:w-[220px] md:shrink-0 md:sticky md:top-[calc(var(--header-height)+1rem)] md:max-h-[calc(100vh-var(--header-height)-1rem)] md:overflow-y-auto bg-white border border-grey-200 rounded-md">
			<div className="px-3 pt-3 pb-2 border-b border-grey-100">
				<div className="relative">
					<SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-grey-400" />
					<input
						type="search"
						value={searchValue}
						onChange={(event) => onSearchChange(event.target.value)}
						placeholder={t('talent_pool.search_placeholder')}
						className="w-full pl-8 pr-2 py-1.5 text-sm bg-white border border-grey-200 rounded placeholder:text-grey-400 focus:outline-none focus:ring-2 focus:ring-brand-600/30 focus:border-brand-600"
					/>
				</div>
			</div>

			<SidebarSection title={t('talent_pool.sidebar.campaigns')}>
				<p className="text-xs text-grey-400 px-1">{t('talent_pool.sidebar.coming_soon')}</p>
			</SidebarSection>

			<SidebarSection title={t('talent_pool.sidebar.last_activity')} last={true}>
				<div className="space-y-1.5">
					{(['anytime', 'last_24h', 'last_7d', 'last_30d'] as const).map((option) => (
						<label
							key={option}
							className="flex items-center gap-2 text-sm text-grey-700 cursor-pointer"
						>
							<input
								type="radio"
								name="last-activity"
								checked={lastActivity === option}
								onChange={() => onLastActivityChange(option)}
								className="size-3.5 accent-brand-600"
							/>
							<span>{t(`talent_pool.sidebar.activity.${option}`)}</span>
						</label>
					))}
				</div>
			</SidebarSection>
		</aside>
	)
}

function SidebarSection({
	title,
	last,
	children,
}: {
	title: string
	last?: boolean
	children: React.ReactNode
}) {
	return (
		<section className={`px-3 py-3 ${last ? '' : 'border-b border-grey-100'}`}>
			<h3 className="text-xs uppercase tracking-wide text-grey-500 font-medium mb-2">
				{title}
			</h3>
			{children}
		</section>
	)
}

function ResultsTable({
	people,
	selectedId,
	onSelect,
}: {
	people: PersonSummaryData[]
	selectedId?: string
	onSelect: (personId: string) => void
}) {
	const { t } = useTranslation('common')

	return (
		<div className="bg-white border border-grey-200 rounded-md overflow-hidden">
			<table className="w-full text-sm">
				<thead className="bg-grey-50 text-grey-600 text-xs uppercase tracking-wide">
					<tr>
						<th className="text-left font-medium px-4 py-3">
							{t('talent_pool.table.candidate')}
						</th>
						<th className="text-left font-medium px-4 py-3">
							{t('talent_pool.table.last_activity')}
						</th>
					</tr>
				</thead>
				<tbody className="divide-y divide-grey-100">
					{people.map((person) => (
						<tr
							key={person.id}
							onClick={() => onSelect(person.id)}
							className={`cursor-pointer ${
								selectedId === person.id ? 'bg-brand-50' : 'hover:bg-grey-50'
							}`}
						>
							<td className="px-4 py-3 align-top">
								<div className="font-medium text-grey-900">{person.name ?? '—'}</div>
								<div className="text-xs text-grey-500 mt-0.5">
									{person.primaryEmail ?? '—'}
									{person.primaryPhone ? ` · ${person.primaryPhone}` : ''}
								</div>
							</td>
							<td className="px-4 py-3 align-top">
								<ActivitySummary
									label={person.lastActivityLabel}
									at={person.lastActivityAt}
									campaignTitle={person.lastActivityCampaign?.title}
									actorName={person.lastActivityActorName}
								/>
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</div>
	)
}

function PaginationBar({
	total,
	currentPage,
	lastPage,
	perPage,
	onPageChange,
}: {
	total: number
	currentPage: number
	lastPage: number
	perPage: number
	onPageChange: (page: number) => void
}) {
	const { t } = useTranslation('common')

	if (total === 0) {
		return null
	}

	const from = (currentPage - 1) * perPage + 1
	const to = Math.min(currentPage * perPage, total)

	const pages = buildPageRange(currentPage, lastPage)

	return (
		<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-sm text-grey-600">
			<div>{t('talent_pool.pagination.range', { from, to, total })}</div>
			<div className="inline-flex items-center gap-1">
				<button
					type="button"
					onClick={() => onPageChange(Math.max(1, currentPage - 1))}
					disabled={currentPage <= 1}
					className="size-9 flex items-center justify-center rounded cursor-pointer text-grey-700 hover:bg-grey-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
					title={t('talent_pool.pagination.previous')}
				>
					<ChevronLeftIcon className="size-4" />
				</button>
				{pages.map((page, index) =>
					page === '…'
						? (
							<span key={`gap-${index}`} className="px-2 text-grey-400">
								…
							</span>
						)
						: (
							<button
								key={page}
								type="button"
								onClick={() => onPageChange(page)}
								className={`min-w-9 h-9 px-2.5 text-sm rounded cursor-pointer ${
									page === currentPage
										? 'bg-brand-600 text-white font-medium'
										: 'text-grey-700 hover:bg-grey-100'
								}`}
							>
								{page}
							</button>
						)
				)}
				<button
					type="button"
					onClick={() => onPageChange(Math.min(lastPage, currentPage + 1))}
					disabled={currentPage >= lastPage}
					className="size-9 flex items-center justify-center rounded cursor-pointer text-grey-700 hover:bg-grey-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
					title={t('talent_pool.pagination.next')}
				>
					<ChevronRightIcon className="size-4" />
				</button>
			</div>
		</div>
	)
}

function buildPageRange(current: number, last: number): Array<number | '…'> {
	if (last <= 7) {
		return Array.from({ length: last }, (_, index) => index + 1)
	}

	const pages: Array<number | '…'> = [1]
	const start = Math.max(2, current - 1)
	const end = Math.min(last - 1, current + 1)

	if (start > 2) {
		pages.push('…')
	}
	for (let page = start; page <= end; page++) {
		pages.push(page)
	}
	if (end < last - 1) {
		pages.push('…')
	}
	pages.push(last)

	return pages
}
